import { useEffect, useRef, useCallback, useState } from "react";

export interface UseBarcodeScanOptions {
  /**
   * 스캔 후 자동으로 입력 필드를 초기화할지 여부
   * @default true
   */
  clearOnScan?: boolean;
  
  /**
   * 스캔 후 자동으로 포커스를 유지할지 여부
   * @default true
   */
  refocusOnScan?: boolean;
  
  /**
   * 디바운스 시간 (ms)
   * 연속 스캔 방지용
   * @default 300
   */
  debounceMs?: number;
  
  /**
   * 자동 포커스 활성화 여부
   * @default true
   */
  autoFocus?: boolean;
  
  /**
   * 입력 시 자동 조회 활성화 여부
   * true일 경우 Enter 키 없이도 입력 시 자동으로 조회
   * @default false
   */
  autoSearchOnInput?: boolean;
  
  /**
   * 자동 조회 디바운스 시간 (ms)
   * autoSearchOnInput이 true일 때 사용
   * @default 500
   */
  autoSearchDebounceMs?: number;
}

export interface UseBarcodeScanReturn {
  /**
   * 스캔 입력 필드에 대한 ref
   */
  scanInputRef: React.RefObject<HTMLInputElement>;
  
  /**
   * 키보드 이벤트 핸들러 (Enter 키 감지)
   */
  handleScanKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  
  /**
   * 입력 이벤트 핸들러 (실시간 값 추적)
   */
  handleInput: (e: React.FormEvent<HTMLInputElement>) => void;
  
  /**
   * 수동으로 포커스를 설정하는 함수
   */
  focus: () => void;
  
  /**
   * 입력 필드를 초기화하는 함수
   */
  clear: () => void;
}

/**
 * PDA 바코드 스캔을 위한 커스텀 훅
 * 
 * @param onScan - 바코드 스캔 시 호출될 콜백 함수
 * @param options - 옵션 설정
 * @returns 스캔 관련 ref와 핸들러
 * 
 * @example
 * ```tsx
 * const { scanInputRef, handleScanKeyDown } = useBarcodeScan((barcode) => {
 *   handleSearch(barcode);
 * });
 * 
 * <input
 *   ref={scanInputRef}
 *   onKeyDown={handleScanKeyDown}
 * />
 * ```
 */
export const useBarcodeScan = (
  onScan: (barcode: string) => void,
  options: UseBarcodeScanOptions = {}
): UseBarcodeScanReturn => {
  const {
    clearOnScan = true,
    refocusOnScan = true,
    debounceMs = 300,
    autoFocus = true,
    autoSearchOnInput = false,
    autoSearchDebounceMs = 500,
  } = options;

  const scanInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentValueRef = useRef<string>("");

  // 자동 포커스
  useEffect(() => {
    if (autoFocus) {
      scanInputRef.current?.focus();
    }
  }, [autoFocus]);

  // 입력값 변경 추적 (onInput 이벤트)
  const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    currentValueRef.current = value;
    
    // 자동 조회가 활성화된 경우
    if (autoSearchOnInput && value.trim()) {
      // 기존 타이머 취소
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
      
      // 디바운스 후 자동 조회
      autoSearchTimerRef.current = setTimeout(() => {
        const trimmedValue = value.trim();
        if (trimmedValue) {
          onScan(trimmedValue);
        }
      }, autoSearchDebounceMs);
    }
  }, [autoSearchOnInput, autoSearchDebounceMs, onScan]);

  // 수동 포커스 함수
  const focus = useCallback(() => {
    scanInputRef.current?.focus();
  }, []);

  // 입력 필드 초기화 함수
  const clear = useCallback(() => {
    if (scanInputRef.current) {
      scanInputRef.current.value = "";
    }
  }, []);

  // Enter 키 감지 및 스캔 처리
  const handleScanKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        
        // ref에서 최신 값을 가져오거나, 없으면 currentTarget에서 가져옴
        const barcode = (currentValueRef.current || e.currentTarget.value || "").trim();
        
        if (!barcode) {
          return;
        }

        // 디바운스 처리
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          // 스캔 콜백 실행
          onScan(barcode);

          // 입력 필드 초기화
          if (clearOnScan && scanInputRef.current) {
            scanInputRef.current.value = "";
            currentValueRef.current = "";
          }

          // 포커스 유지
          if (refocusOnScan) {
            setTimeout(() => {
              scanInputRef.current?.focus();
            }, 100);
          }
        }, debounceMs);
      }
    },
    [onScan, clearOnScan, refocusOnScan, debounceMs]
  );

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
    };
  }, []);

  return {
    scanInputRef,
    handleScanKeyDown,
    handleInput,
    focus,
    clear,
  };
};

